import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  AfterViewInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { FormGroup, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  CompositeFilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import { MaskService } from '../../services/mask.service';
import { states } from '../../lib/states';
import { DateTime } from 'luxon';
import { CustomFiltersService } from '../../services/custom-filters.service';
import { CaseManagementService } from '../../case-management/case-management.service';
import { BookManagementService } from '../../book-management/book-management.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { RbGridService } from '../rb-grid/rb-grid.service';
import uniqBy from 'lodash-es/uniqBy';
import isEmpty from 'lodash-es/isEmpty';
import { GridFilterFieldsComponent } from './grid-filter-fields/grid-filter-fields.component';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ManageFiltersComponent } from '../../manage-filters/manage-filters.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-grid-filters',
  templateUrl: './grid-filters.component.html',
  styleUrls: ['./grid-filters.component.scss'],
  imports: [
    GridFilterFieldsComponent,
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatCheckboxModule,
    ManageFiltersComponent,
    MatMenuModule,
    MatTooltipModule,
    MatButtonModule,
  ],
  providers: [],
})
export class GridFiltersComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  fb = inject(FormBuilder);
  cstmFltrSrvc = inject(CustomFiltersService);
  bmSvc = inject(BookManagementService);
  ss = inject(SessionStorageService);
  private mask = inject(MaskService);
  caseManagementService = inject(CaseManagementService);
  gridSrvc = inject(RbGridService);
  changeDetector = inject(ChangeDetectorRef);

  @Input() defaultViewSet = false;
  @Input() filters;
  @Input() reset;
  @Input() manualSearch;
  @Input() preexistingFilters;
  @Input() bridgeId;
  @Input() showBridgeIdSearch;
  @Input() searchButtonText: string;
  @Input() defaultEmptyGrid: boolean;
  @Input() gridName: string;
  @Input() showFilterManager: boolean;
  @Input() disableSearch: boolean;
  @Input() colFiltersChanged: { values: any; dataField: string };
  @Input() selectedTemplateVar: any;
  @Input() hiddenCols: string[];
  @Input() colSort: SortDescriptor[] | undefined;
  @Input() colOrder: string[];
  @Output() filtersUpdated = new EventEmitter();
  @Output() triggerSearch = new EventEmitter();
  @Output() bridgeIdSearch = new EventEmitter();
  @Output() additionalData = new EventEmitter();
  @Output() hiddenColsEmitter = new EventEmitter();
  @Output() colSortEmitter = new EventEmitter();
  @Output() colOrderEmitter = new EventEmitter();

  filterForm: FormGroup = this.fb.group({});
  inputValueChanged: Subject<string> = new Subject<string>();
  formFields = { primaryForm: [], secondaryForm: [] };
  additionalFilters = [];
  currentFilters: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [],
  };
  dollarMask = this.mask.dollarMaskSpecs();
  percentMask = this.mask.percentMaskSpecs();
  states = states;
  unsubscribe = new Subject();
  textChangeEv: any;
  textChangeField: any;
  globals = this.ss.get('globals');
  defaultUser = this.globals ? this.globals.user : null;
  bridgeIdOptions = [];
  advancedFilters = false;

  ngOnInit(): void {
    this.filterForm = this.createGroup();

    if (this.filters) {
      this.buildFilters();
      this.setDefaultValues();
    }

    this.cstmFltrSrvc.reloadFilters$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.applySavedFilters(this.preexistingFilters);
        },
        error: error => alert(error),
      });

    this.cstmFltrSrvc.updateDropdowns$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: field => {
          if (field && field['values']?.['value']) {
            this.currentFilters.filters = this.currentFilters.filters.filter(
              filter => filter['field'] !== field['field']
            );
            this.currentFilters.filters.push({
              field: field['values']?.['field'],
              operator: 'equal',
              value: field['values']?.['value'],
            });
          }
        },
        error: error => alert(error),
      });

    this.cstmFltrSrvc.updatePresets$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: field => {
          if (field && field['customRange']) {
            this.currentFilters.filters = this.currentFilters.filters.filter(
              filter => filter['field'] !== field['field']
            );
            this.currentFilters.filters.push(
              {
                field: field['field'],
                operator: 'start',
                value: field['customRange']['start'],
              },
              {
                field: field['field'],
                operator: 'end',
                value: field['customRange']['end'],
              }
            );
          }

          if (
            field &&
            field['value'] &&
            (field['value'].min || field['value'].max)
          ) {
            this.currentFilters.filters.push(
              {
                field: field['field'],
                operator: 'gte',
                value: field['value'].min,
              },
              {
                field: field['field'],
                operator: 'lte',
                value: field['value'].max,
              }
            );
          }
        },
        error: error => alert(error),
      });

    this.cstmFltrSrvc.updateRepReport$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: reportVal => {
          if (reportVal && reportVal['value']) {
            this.currentFilters.filters = this.currentFilters.filters.filter(
              filter => filter['field'] !== reportVal['field']
            );
            this.currentFilters.filters.push({
              field: reportVal['field'],
              operator: 'equal',
              value: reportVal['value'],
            });
          }
          this.filtersChanged();
        },
        error: error => alert(error),
      });

    this.cstmFltrSrvc.updateSolution$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: solutionVal => {
          if (solutionVal && solutionVal['value']) {
            this.currentFilters.filters = this.currentFilters.filters.filter(
              filter => filter['field'] !== solutionVal['field']
            );
            this.currentFilters.filters.push({
              field: solutionVal['field'],
              operator: 'equal',
              value: solutionVal['value'],
            });
          }
          this.filtersChanged();
        },
        error: error => alert(error),
      });

    this.cstmFltrSrvc.updateValueRange$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: field => {
          if (field && field['customRange']) {
            this.currentFilters.filters = this.currentFilters.filters.filter(
              filter => filter['field'] !== field['field']
            );
            this.currentFilters.filters.push(
              {
                field: field['field'],
                operator: 'start',
                value: field['customRange']['start'],
              },
              {
                field: field['field'],
                operator: 'end',
                value: field['customRange']['end'],
              }
            );
          }

          if (
            field &&
            field['value'] &&
            field['value'].min &&
            !field['value'].max
          ) {
            this.currentFilters.filters.push(
              {
                field: field['field'],
                operator: 'gte',
                value: field['value'].min,
              },
              {
                field: field['field'],
                operator: 'lte',
                value: 99999999,
              }
            );
          }

          if (
            field &&
            field['value'] &&
            !field['value'].min &&
            field['value'].max
          ) {
            this.currentFilters.filters.push(
              {
                field: field['field'],
                operator: 'gte',
                value: 0,
              },
              {
                field: field['field'],
                operator: 'lte',
                value: field['value'].max,
              }
            );
          }

          if (
            field &&
            field['value'] &&
            field['value'].min &&
            field['value'].max
          ) {
            this.currentFilters.filters.push(
              {
                field: field['field'],
                operator: 'gte',
                value: field['value'].min,
              },
              {
                field: field['field'],
                operator: 'lte',
                value: field['value'].max,
              }
            );
          }
        },
        error: error => alert(error),
      });

    this.cstmFltrSrvc.updateGroupBy$.subscribe((val: string) => {
      if (val) {
        this.currentFilters.filters = this.currentFilters.filters.filter(
          filter => filter['field'] !== 'group'
        );
        this.currentFilters.filters.push({
          field: 'group',
          operator: 'equal',
          value: val,
        });
        this.preexistingFilters.filters =
          this.preexistingFilters.filters.filter(
            filter => filter['field'] !== 'group'
          );
        this.preexistingFilters.filters.push({
          field: 'group',
          operator: 'equal',
          value: val,
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        switch (propName) {
          case 'filters': {
            this.filterForm = this.createGroup();
            this.buildFilters();
            break;
          }
          case 'reset': {
            if (changes[propName].currentValue) {
              this.filterForm.reset();
              this.currentFilters = { logic: 'and', filters: [] };
            }
            break;
          }
          case 'colFiltersChanged': //IW product grid sync any table header filter changes with external
            if (changes[propName].currentValue) {
              let controlFound;
              const primaryControls =
                this.filterForm.controls?.primaryForm?.['controls'];
              if (primaryControls) {
                controlFound = Object.keys(primaryControls).find(
                  control =>
                    control === changes[propName].currentValue.dataField
                );
              }
              if (controlFound) {
                if (
                  changes[propName].currentValue.filterData.externalFilter &&
                  changes[propName].currentValue.filterData.externalFilter
                    .controlType === 'multiselect'
                ) {
                  this.filterForm.controls.primaryForm['controls'][
                    controlFound
                  ].setValue(changes[propName].currentValue.values);
                } else {
                  this.filterForm.controls.primaryForm['controls'][
                    controlFound
                  ].setValue(changes[propName].currentValue.values[0]);
                }
                this.currentFilters =
                  changes[propName].currentValue.currentFilters;
              } else {
                const secondaryControls =
                  this.filterForm.controls?.secondaryForm?.['controls'];
                if (secondaryControls) {
                  controlFound = Object.keys(secondaryControls).find(
                    control =>
                      control === changes[propName].currentValue.dataField
                  );
                }
                if (controlFound) {
                  if (
                    changes[propName].currentValue.filterData.externalFilter &&
                    changes[propName].currentValue.filterData.externalFilter
                      .controlType === 'multiselect'
                  ) {
                    this.filterForm.controls.secondaryForm['controls'][
                      controlFound
                    ].setValue(changes[propName].currentValue.values);
                  } else {
                    this.filterForm.controls.secondaryForm['controls'][
                      controlFound
                    ].setValue(changes[propName].currentValue.values[0]);
                  }
                  this.currentFilters =
                    changes[propName].currentValue.currentFilters;
                }
              }
            }
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (
      ((this.defaultViewSet &&
        this.preexistingFilters &&
        this.preexistingFilters?.filters?.length > 0) ||
        (this.preexistingFilters &&
          this.preexistingFilters?.filters?.length > 0)) &&
      !this.defaultEmptyGrid
    ) {
      this.applySavedFilters(this.preexistingFilters, null, true);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  createGroup(groupFilter?) {
    const group = this.fb.group({});
    if (groupFilter) {
      let controls = this.filters.filter(x => x.filterGroup === groupFilter);

      // If there are additional filters add them to the primary formgroup
      if (
        groupFilter === 'primary' &&
        this.filters.find(x => x.filterGroup === 'additional')
      ) {
        controls = controls.concat(
          this.filters.filter(x => x.filterGroup === 'additional')
        );
      }

      controls.forEach(control => {
        group.addControl(control.varName, this.fb.control(''));
        if (control['relatedVarName']) {
          group.addControl(control.relatedVarName, this.fb.control(''));
        }
      });
    }

    return group;
  }

  createControl() {
    return this.fb.control('');
  }

  buildFilters() {
    this.currentFilters = { logic: 'and', filters: [] };
    this.formFields.primaryForm = this.filters.filter(
      x => x.filterGroup === 'primary'
    );
    this.formFields.secondaryForm = this.filters.filter(
      x => x.filterGroup === 'secondary'
    );
    if (this.filters.find(filter => filter.filterGroup === 'additional')) {
      this.additionalFilters = this.filters.filter(
        filter => filter.filterGroup === 'additional'
      );
    }
    let groups = this.filters.map(x => x.filterGroup);
    groups = [...new Set(groups)];
    if (!this.filterForm.controls.primaryForm && groups.includes('primary')) {
      this.filterForm.addControl('primaryForm', this.createGroup('primary'));
    } else if (!groups.includes('primary')) {
      delete this.formFields.primaryForm;
    }

    if (
      !this.filterForm.controls.secondaryForm &&
      groups.includes('secondary')
    ) {
      this.filterForm.addControl(
        'secondaryForm',
        this.createGroup('secondary')
      );
    } else if (!groups.includes('secondary')) {
      delete this.formFields.secondaryForm;
    }

    if (
      this.preexistingFilters &&
      this.preexistingFilters.filters?.length > 0
    ) {
      // If there are preexisting filters set them in the form
      this.currentFilters = this.preexistingFilters;
      const primaryFiltersObject = {};
      const secondaryFiltersObject = {};
      this.filters.map(filter => {
        if (filter['filterGroup'] !== 'secondary') {
          primaryFiltersObject[filter.varName] = '';
          if (filter['relatedVarName']) {
            primaryFiltersObject[filter.relatedVarName] = '';
          }
        } else {
          secondaryFiltersObject[filter.varName] = '';
          if (filter['relatedVarName']) {
            secondaryFiltersObject[filter.relatedVarName] = '';
          }
        }
      });
      // If there is a customRange filter format it properly
      if (this.currentFilters.filters.find(filter => filter['customRange'])) {
        const customRangeFilter = this.currentFilters.filters.find(
          filter => filter['customRange']
        );
        this.currentFilters.filters.push({
          field: customRangeFilter['field'],
          operator: 'start',
          value: customRangeFilter['customRange']['start'],
        });
        this.currentFilters.filters.push({
          field: customRangeFilter['field'],
          operator: 'end',
          value: customRangeFilter['customRange']['end'],
        });

        this.currentFilters.filters.splice(
          this.currentFilters.filters.findIndex(
            filter => filter['customRange']
          ),
          1
        );

        if (
          Object.keys(primaryFiltersObject).includes(
            customRangeFilter['field']
          ) &&
          !primaryFiltersObject[customRangeFilter['field']]
        ) {
          primaryFiltersObject[customRangeFilter['field']] =
            customRangeFilter['customRange']['start'];
        }
      }
      this.currentFilters.filters.map(filter => {
        if (
          filter &&
          Object.keys(primaryFiltersObject).includes(filter['field'])
        ) {
          primaryFiltersObject[filter['field']] = filter['value'];

          const formLevel = 'primary';
          const leGroup = this.createGroup(formLevel);
          leGroup.setValue(primaryFiltersObject);
          this.filterForm.setControl(`${formLevel}Form`, leGroup);
        } else if (
          filter &&
          Object.keys(secondaryFiltersObject).includes(filter['field'])
        ) {
          secondaryFiltersObject[filter['field']] = filter['value'];

          const formLevel = 'secondary';
          const leGroup = this.createGroup(formLevel);
          leGroup.setValue(secondaryFiltersObject);
          this.filterForm.setControl(`${formLevel}Form`, leGroup);
        }
      });
    }
  }

  setDefaultValues() {
    const defaultValues = this.filters.filter(x => x.defaultValue);

    if (defaultValues.length > 0) {
      // If there are default values set them in the form
      defaultValues.map(filter => {
        if (this.gridName === 'income') {
          const preexistingFilterFound = this.preexistingFilters?.filters?.find(
            filterItem => filterItem.field === filter.varName
          );
          if (preexistingFilterFound) {
            this.filterForm.controls[`${filter.filterGroup}Form`]['controls'][
              filter.varName
            ].setValue(preexistingFilterFound.value);
          } else {
            this.filterForm.controls[`${filter.filterGroup}Form`]['controls'][
              filter.varName
            ].setValue(filter.defaultValue);
            this.currentFilters.filters.push({
              field: filter.varName,
              operator: filter.filterType,
              value: filter.defaultValue,
            });
          }
        } else {
          this.filterForm.controls[`${filter.filterGroup}Form`]['controls'][
            filter.varName
          ].setValue(filter.defaultValue);
          this.currentFilters.filters.push({
            field: filter.varName,
            operator: filter.filterType,
            value: filter.defaultValue,
          });
        }
      });

      this.filtersChanged();
    }
  }

  selectionChange(ev, field) {
    if (this.gridName === 'IWProductShelf') {
      this.currentFilters.filters = this.gridSrvc.flattenAndRemoveDuplicates(
        this.currentFilters
      );
    }
    const isArray = Array.isArray(ev.value);
    const eventFieldName =
      ev.source?.ngControl && ev.source?.ngControl?.name
        ? ev.source.ngControl.name
        : field.fullField.DataField;
    const existingIdx = this.currentFilters.filters.findIndex(
      x => eventFieldName === x['field']
    );

    const formGroup =
      field.filterGroup === 'additional'
        ? 'primaryForm'
        : `${field.filterGroup}Form`;

    if (!isArray) {
      if (existingIdx > -1) {
        if (!(ev.value || (ev.option.value && ev.option.value.length))) {
          this.filterForm.controls[formGroup]['controls'][
            field.varName
          ].setValue(null);
          this.currentFilters.filters = this.currentFilters.filters.filter(
            filter => filter['field'] !== field.varName
          );
        } else {
          this.filterForm.controls[`${formGroup}`]['controls'][
            field.varName
          ].setValue(ev.value || ev.option.value);
          this.currentFilters.filters[existingIdx]['value'] =
            ev.value || ev.option.value;
        }
      } else if (field.filterType !== 'between') {
        this.filterForm.controls[`${formGroup}`]['controls'][
          field.varName
        ].setValue(ev.value || ev.option.value);
        this.currentFilters.filters.push({
          field: eventFieldName,
          operator: field.filterType,
          value: ev.value || ev.option.value,
        });
      } else {
        this.currentFilters.filters.push(
          {
            field: eventFieldName,
            operator: 'lte',
            value: +field.between1,
          },
          {
            field: eventFieldName,
            operator: 'gte',
            value: +field.between2,
          }
        );
      }
    } else if (
      field.controlType === 'multiselect' &&
      (field.varName === 'AlertIDs' ||
        field.varName === 'StatusName' ||
        field.varName === 'ScoreClass' ||
        field.varName === 'FundFamilyName')
    ) {
      if (existingIdx > -1) {
        this.currentFilters.filters = this.currentFilters.filters.filter(
          x => x['field'] !== eventFieldName
        );
        this.currentFilters.filters.push({
          field: eventFieldName,
          operator: 'selected',
          value: ev.value || ev.option.value,
        });
      } else {
        ev.value.forEach(() => {
          this.currentFilters.filters.push({
            field: eventFieldName,
            operator: 'selected',
            value: ev.value || ev.option.value,
          });
        });
      }
    } else {
      if (existingIdx > -1) {
        this.currentFilters.filters = this.currentFilters.filters.filter(
          x => x['field'] !== eventFieldName
        );
        ev.value.forEach(filterValue => {
          if (!Array.isArray(filterValue)) {
            this.currentFilters.filters.push({
              field: eventFieldName,
              operator: 'contains',
              value: filterValue,
            });
          }
        });
      } else {
        ev.value.forEach(filterValue => {
          if (!Array.isArray(filterValue)) {
            this.currentFilters.filters.push({
              field: eventFieldName,
              operator: 'contains',
              value: filterValue,
            });
          }
        });
      }
    }
    this.filtersChanged();
  }

  clearFilter(ev, field, group) {
    ev.preventDefault();
    ev.stopPropagation();
    this.filterForm.controls[group]['controls'][field.varName].setValue(null);
    this.currentFilters.filters = this.currentFilters.filters.filter(
      filter => filter['field'] !== field.varName
    );

    if (field.between1) {
      this.currentFilters.filters = this.currentFilters.filters.filter(
        filter => filter['field'] !== field.between1
      );
    }

    if (field.between2) {
      this.currentFilters.filters = this.currentFilters.filters.filter(
        filter => filter['field'] !== field.between2
      );
    }
    this.filtersChanged();
  }

  textChange(ev, field) {
    ev.preventDefault();
    this.textChangeEv = ev;
    this.textChangeField = field;

    this.inputValueChanged
      .pipe(debounceTime(750), distinctUntilChanged())
      .subscribe(() => {
        if (!this.manualSearch && this.inputValueChanged.observed) {
          this.textEval(this.textChangeEv, this.textChangeField);
        }
      });

    if (ev.target.value === '' || ev.target.value === '$') {
      const filterGroup =
        field.filterGroup === 'additional'
          ? 'primaryForm'
          : `${field.filterGroup}Form`;
      this.clearFilter(ev, field, filterGroup);
    } else {
      this.inputValueChanged.next(ev.target.value);
    }
    this.filtersChanged();
  }

  textEval(ev, field) {
    const existingIdx = this.currentFilters.filters.findIndex(
      x => field.fullField.DataField === x['field']
    );
    const fltrValue = ev.target.value.includes('$')
      ? this.mask.removeMoneyMask(ev.target.value)
      : ev.target.value;

    if (existingIdx > -1 && !ev.target.value) {
      this.currentFilters.filters.splice(existingIdx, 1);
    } else if (
      existingIdx > -1 &&
      field.filterType !== 'between' &&
      ev.target.value
    ) {
      this.currentFilters.filters[existingIdx]['value'] = ev.target.value;
    } else if (existingIdx < 0 && field.filterType === 'between') {
      if (fltrValue) {
        this.currentFilters.filters.push(
          { field: field.between1, operator: 'lte', value: +fltrValue },
          { field: field.between2, operator: 'gte', value: +fltrValue }
        );
      } else {
        this.currentFilters.filters = this.currentFilters.filters.filter(
          existingFilter =>
            existingFilter['field'] !== field.between1 &&
            existingFilter['field'] !== field.between2
        );
      }
    } else if (existingIdx > -1 && field.filterType === 'between') {
      if (fltrValue) {
        const fltr1 = this.currentFilters.filters.filter(
          fld => fld['field'] === field.fullField.externalFilter.between1
        );
        fltr1[0]['value'] = +fltrValue;

        const fltr2 = this.currentFilters.filters.filter(
          fld => fld['field'] === field.fullField.externalFilter.between2
        );
        fltr2[0]['value'] = +fltrValue;
      } else {
        this.currentFilters.filters = this.currentFilters.filters.filter(
          existingFilter =>
            existingFilter['field'] !== field.between1 &&
            existingFilter['field'] !== field.between2
        );
      }
    } else if (existingIdx < 0 && field.filterType === 'contains') {
      this.currentFilters.filters.push({
        field: field.varName,
        operator: 'contains',
        value: ev.target.value,
      });
    } else {
      this.currentFilters.filters.push({
        field: field.fullField.DataField,
        operator: field.filterType,
        value: ev.target.value,
      });
    }

    this.filtersChanged();
  }

  valueRangeChange(ev, field) {
    ev.preventDefault();
    if (this.gridName === 'IWProductShelf') {
      this.currentFilters.filters = this.gridSrvc.flattenAndRemoveDuplicates(
        this.currentFilters
      );
    }

    const existingIdx = this.currentFilters.filters.findIndex(
      x => field.fullField.DataField === x['field']
    );
    const fltrValue = ev.valueRange;

    if (!fltrValue.min && fltrValue.max) {
      fltrValue.min = `0`;
    }

    if (!fltrValue.max && fltrValue.min) {
      fltrValue.max = `99999999`;
    }

    const valKeys = Object.keys(ev.valueRange);
    const formGroup =
      field.filterGroup === 'additional'
        ? 'primaryForm'
        : `${field.filterGroup}Form`;

    if (field.controlType === 'currencyRange') {
      valKeys.map(keyName => {
        fltrValue[keyName] = `${this.mask.removeMoneyMask(fltrValue[keyName])}`;
      });
    }

    if (field.controlType === 'percentRange') {
      valKeys.map(keyName => {
        fltrValue[keyName] = `${this.mask.removePercentMask(
          fltrValue[keyName]
        )}`;
      });
    }

    if (existingIdx < 0) {
      this.currentFilters.filters.push(
        { field: field.varName, operator: 'gte', value: fltrValue.min },
        { field: field.varName, operator: 'lte', value: fltrValue.max }
      );
    } else if (
      existingIdx >= 0 &&
      this.currentFilters.filters[existingIdx]['value']['min'] &&
      !this.currentFilters.filters[existingIdx]['value']['max']
    ) {
      this.currentFilters.filters.splice(existingIdx, 1);
      this.currentFilters.filters.push(
        { field: field.varName, operator: 'gte', value: fltrValue.min },
        { field: field.varName, operator: 'lte', value: 99999999 }
      );
    } else if (
      existingIdx >= 0 &&
      !this.currentFilters.filters[existingIdx]['value']['min'] &&
      this.currentFilters.filters[existingIdx]['value']['max']
    ) {
      this.currentFilters.filters.splice(existingIdx, 1);
      this.currentFilters.filters.push(
        { field: field.varName, operator: 'gte', value: 0 },
        { field: field.varName, operator: 'lte', value: fltrValue.max }
      );
    } else if (
      existingIdx >= 0 &&
      this.currentFilters.filters[existingIdx]['value']['min'] &&
      this.currentFilters.filters[existingIdx]['value']['max']
    ) {
      this.currentFilters.filters.splice(existingIdx, 1);
      this.currentFilters.filters.push(
        { field: field.varName, operator: 'gte', value: fltrValue.min },
        { field: field.varName, operator: 'lte', value: fltrValue.max }
      );
    } else {
      const filterMin = this.currentFilters.filters.filter(
        existingFilter =>
          existingFilter['field'] === field.varName &&
          existingFilter['operator'] === 'gte'
      );
      const filterMax = this.currentFilters.filters.filter(
        existingFilter =>
          existingFilter['field'] === field.varName &&
          existingFilter['operator'] === 'lte'
      );
      filterMin[0]['value'] = fltrValue.min;
      filterMax[0]['value'] = fltrValue.max;
    }

    if (!fltrValue.min && !fltrValue.max) {
      this.currentFilters.filters = this.currentFilters.filters.filter(
        filter => filter['field'] !== field.varName
      );
      this.filterForm.controls[formGroup]['controls'][field.varName].setValue(
        null
      );
    } else {
      this.filterForm.controls[formGroup]['controls'][field.varName].setValue({
        min: fltrValue.min,
        max: fltrValue.max,
      });
    }

    this.filtersChanged();
  }

  dateChange(e, field) {
    if (this.gridName === 'IWProductShelf') {
      this.currentFilters.filters = this.gridSrvc.flattenAndRemoveDuplicates(
        this.currentFilters
      );
    }
    let dateValue;
    const position = e.targetElement.className.includes('start-date')
      ? 'start'
      : 'end';
    const filterPosition = checkField =>
      this.currentFilters.filters.findIndex(
        filter =>
          filter['field'] === field.varName && filter['operator'] === checkField
      );
    if (e.value !== null) {
      if (e.targetElement.className.includes('start-date')) {
        dateValue = DateTime.fromJSDate(new Date(e.value))
          .startOf('day')
          .toUTC()
          .toISO();
      } else {
        dateValue = DateTime.fromJSDate(new Date(e.value))
          .endOf('day')
          .toUTC()
          .toISO();
      }
    } else {
      dateValue = null;
    }

    const filterExists = filterPosition(position);

    const filterObj = {
      field: field.varName,
      operator: position,
      value: dateValue,
    };

    const formGroup =
      field.filterGroup === 'additional'
        ? 'primaryForm'
        : `${field.filterGroup}Form`;

    if (field.controlType === 'presetRange' && e.presetSelection) {
      filterObj['presetSelection'] = e.presetSelection;
      this.filterForm.controls[formGroup]['controls'][field.varName].setValue(
        e.presetSelection
      );
    }

    if (filterExists >= 0) {
      this.currentFilters.filters[filterExists] = filterObj;
    } else {
      this.currentFilters.filters.push(filterObj);
    }
    if (field.controlType === 'relatedDate') {
      // Clear the related date
      this.currentFilters.filters = this.currentFilters.filters.filter(
        relatedFilter => relatedFilter['field'] !== field.relatedVarName
      );
    }

    if (
      e.value === null &&
      this.currentFilters.filters.find(
        filter => filter['field'] === field.varName && !filter['value']
      )
    ) {
      this.currentFilters.filters = this.currentFilters.filters.filter(
        filter => filter['field'] !== field.relatedVarName && filter['value']
      );
      this.filterForm.controls[formGroup]['controls'][field.varName].setValue(
        null
      );
      this.filtersChanged();
    }
    if (
      e.targetElement.className.includes('end-date') &&
      filterPosition('start') >= 0 &&
      filterPosition('end') >= 0
    ) {
      this.filtersChanged();
    }
  }

  relatedDateChange(e, field) {
    if (this.gridName === 'IWProductShelf') {
      this.currentFilters.filters = this.gridSrvc.flattenAndRemoveDuplicates(
        this.currentFilters
      );
    }
    let dateValue;
    let position;

    const filterPosition = checkField =>
      this.currentFilters.filters.findIndex(
        filter =>
          filter['field'] === field.relatedVarName &&
          filter['operator'] === checkField
      );

    if (e.targetElement.className.includes('start-date')) {
      dateValue = DateTime.fromJSDate(new Date(e.value))
        .startOf('day')
        .toUTC()
        .toISO();
      position = 'start';
    } else {
      dateValue = DateTime.fromJSDate(new Date(e.value))
        .endOf('day')
        .toUTC()
        .toISO();
      position = 'end';
    }

    const filterExists = filterPosition(position);

    if (filterExists >= 0) {
      this.currentFilters.filters[filterExists] = {
        field: field.relatedVarName,
        operator: position,
        value: dateValue,
      };
    } else {
      this.currentFilters.filters.push({
        field: field.relatedVarName,
        operator: position,
        value: dateValue,
      });
    }

    // Clear the related date
    this.currentFilters.filters = this.currentFilters.filters.filter(
      relatedFilter => relatedFilter['field'] !== field.varName
    );

    if (
      e.targetElement.className.includes('end-date') &&
      filterPosition('start') >= 0 &&
      filterPosition('end') >= 0
    ) {
      this.filtersChanged();
    }
  }

  filtersChanged(afterInit = false) {
    //Next line logic could be refactored. This is a quick hack
    //to get the manage filter child component to detect that this.currentFilters
    //has been changed so it updates in the modal properly (only changing the contents
    //of this object does not trigger the detection).
    //
    //We may need ngDoCheck() for this if we decide to change it.
    this.currentFilters = JSON.parse(JSON.stringify(this.currentFilters));
    if (afterInit) {
      Object.assign(this.currentFilters, {
        additionalData: { firstUpdate: afterInit },
      });
    }
    this.filtersUpdated.emit(this.currentFilters);
  }

  triggerManualSearch() {
    if (this.textChangeEv && this.textChangeField) {
      this.textEval(this.textChangeEv, this.textChangeField);
      this.textChangeEv = null;
      this.textChangeField = null;
    }
    this.triggerSearch.emit();
  }

  setBridgeIdSearch(e, clearBridge = false) {
    if (clearBridge) {
      this.bridgeId = null;
      this.bridgeIdSearch.emit('clear');
    } else {
      this.bridgeId = e.value;
      this.bridgeIdSearch.emit(e);
    }
  }

  applySavedFilters(ev, manualChange = false, afterInit = false) {
    this.preexistingFilters = ev;
    this.filterForm.reset();
    this.currentFilters = { logic: 'and', filters: [] };
    this.cstmFltrSrvc.updatePresetDateValues(null);
    this.cstmFltrSrvc.updateDropdownValues(null, null);
    this.cstmFltrSrvc.updateValueRanges(null);

    const presetRangeFilters = this.filters.filter(filterItem => {
      return filterItem.controlType === 'presetRange';
    });
    const dropdownFilterItems = this.filters.filter(filterItem => {
      return (
        filterItem.controlType === 'npn' ||
        filterItem.controlType === 'chip' ||
        filterItem.controlType === 'multiselect' ||
        filterItem.controlType === 'dropdown' ||
        filterItem.controlType === 'hashedInput' ||
        filterItem.controlType === 'unit' ||
        filterItem.controlType === 'user' ||
        filterItem.controlType === 'repCode' ||
        filterItem.controlType === 'repCodeMulti' ||
        filterItem.controlType === 'unitMulti' ||
        filterItem.controlType === 'bridgeidselect'
      );
    });
    this.preexistingFilters.filters.forEach(preexitingFilterItem => {
      if ('solutionId' === preexitingFilterItem.field) {
        this.cstmFltrSrvc.updateSolutionSelection(preexitingFilterItem.value);
      } else if ('name' === preexitingFilterItem.field) {
        this.cstmFltrSrvc.updateRepReportSelection(preexitingFilterItem.value);
      }
    });
    if (dropdownFilterItems.length > 0) {
      this.preexistingFilters.filters.forEach(preexistingFilterItem => {
        dropdownFilterItems.forEach(dropdownFilterItem => {
          if (dropdownFilterItem.varName === preexistingFilterItem.field) {
            this.cstmFltrSrvc.updateDropdownValues(
              preexistingFilterItem,
              preexistingFilterItem.field
            );
          }
        });
      });
      // find empty dropdowns and clear them
      dropdownFilterItems.filter(item => {
        if (
          !this.preexistingFilters.filters.find(
            element => element.field === item.varName
          )
        ) {
          this.cstmFltrSrvc.updateDropdownValues(null, item.varName);
        }
      });
    }

    if (presetRangeFilters.length > 0) {
      this.preexistingFilters.filters.forEach(preexistingFilterItem => {
        presetRangeFilters.forEach(presetFilterItem => {
          if (presetFilterItem.varName === preexistingFilterItem.field) {
            if (preexistingFilterItem.presetSelection) {
              this.cstmFltrSrvc.updatePresetDateValues(
                this.preexistingFilters.filters.find(
                  filter =>
                    filter.operator === 'start' &&
                    filter.field === preexistingFilterItem.field
                )
              );
              this.cstmFltrSrvc.updatePresetDateValues(
                this.preexistingFilters.filters.find(
                  filter =>
                    filter.operator === 'end' &&
                    filter.field === preexistingFilterItem.field
                )
              );
              this.cstmFltrSrvc.updatePresetDateValues(
                this.preexistingFilters.filters.find(
                  filter => filter.presetSelection
                )
              );
            } else {
              this.cstmFltrSrvc.updatePresetDateValues(
                this.preexistingFilters.filters.find(
                  filter =>
                    filter.operator === 'start' &&
                    filter.field === preexistingFilterItem.field
                )
              );
              this.cstmFltrSrvc.updatePresetDateValues(
                this.preexistingFilters.filters.find(
                  filter =>
                    filter.operator === 'end' &&
                    filter.field === preexistingFilterItem.field
                )
              );
              this.cstmFltrSrvc.updatePresetDateValues('custom');
            }
          }
        });
      });
    }

    if (
      this.preexistingFilters &&
      this.preexistingFilters.filters.find(filter => filter.customRange)
    ) {
      this.cstmFltrSrvc.updatePresetDateValues(
        this.preexistingFilters.filters.find(filter => filter.customRange)
      );
    } else if (
      this.preexistingFilters &&
      this.preexistingFilters.filters.find(
        filter =>
          !(filter?.['value'] instanceof Object) &&
          typeof filter?.['value'] === 'string' &&
          (filter?.['value']?.includes('days') ||
            filter?.['value']?.includes('td'))
      )
    ) {
      this.cstmFltrSrvc.updatePresetDateValues(
        this.preexistingFilters.filters.find(
          filter =>
            filter['value'].includes('days') || filter['value'].includes('td')
        )
      );
    }

    if (
      this.preexistingFilters &&
      this.preexistingFilters.filters.find(
        filter => filter.operator === 'start'
      ) &&
      this.preexistingFilters.filters.find(
        filter => filter.operator === 'end'
      ) &&
      !this.preexistingFilters.filters.find(filter => filter.customRange)
    ) {
      this.cstmFltrSrvc.updatePresetDateValues(
        this.preexistingFilters.filters.find(
          filter => filter.operator === 'start'
        )
      );
      this.cstmFltrSrvc.updatePresetDateValues(
        this.preexistingFilters.filters.find(
          filter => filter.operator === 'end'
        )
      );
    }

    if (
      this.preexistingFilters &&
      !this.preexistingFilters.filters.find(
        filter => filter.operator === 'start'
      ) &&
      !this.preexistingFilters.filters.find(
        filter => filter.operator === 'end'
      ) &&
      !this.preexistingFilters.filters.find(filter => filter.customRange)
    ) {
      this.cstmFltrSrvc.updatePresetDateValues(null);
    }

    if (
      this.preexistingFilters &&
      this.preexistingFilters.filters.find(
        filter => filter.operator === 'equal'
      )
    ) {
      this.cstmFltrSrvc.updateDropdownValues(
        this.preexistingFilters.filters.find(
          filter => filter.operator === 'equal'
        ),
        this.preexistingFilters.filters.find(
          filter => filter.operator === 'equal'
        ).varName
      );
    }

    if (
      this.preexistingFilters &&
      this.preexistingFilters.filters.find(
        filter => filter.value?.max || filter.value?.min
      )
    ) {
      this.preexistingFilters.filters
        .filter(filter => filter.value?.max || filter.value?.min)
        .forEach(filterItem => {
          this.cstmFltrSrvc.updateValueRanges(filterItem);
        });
    }
    if (
      this.preexistingFilters &&
      this.preexistingFilters.filters.find(
        filter => filter.operator === 'gte' || filter.operator === 'lte'
      )
    ) {
      this.preexistingFilters.filters
        .filter(filter => filter.operator === 'gte')
        .forEach(filterItem => {
          this.cstmFltrSrvc.updateValueRanges(filterItem);
        });
      this.preexistingFilters.filters
        .filter(filter => filter.operator === 'lte')
        .forEach(filterItem => {
          this.cstmFltrSrvc.updateValueRanges(filterItem);
        });
    }

    const groupByVal = this.preexistingFilters?.filters.find(
      filterItem => filterItem.field === 'group'
    );
    if (groupByVal) {
      this.cstmFltrSrvc.updateGroupByValue(groupByVal.value);
    }

    this.buildFilters();
    this.filtersChanged(afterInit);
    this.changeDetector.detectChanges();

    if (manualChange) {
      this.triggerSearch.emit();
    }
  }

  toggleAdvancedFilters(ev) {
    this.advancedFilters = ev.checked;
  }

  handleAdditionalData(ev) {
    // function for data from fields that need to pass data back up.
    // add to this as necessary

    this.additionalData.emit(ev);
  }

  moreFilterCount() {
    const uniqueFilters = [];
    let filterCount = 0;

    uniqBy(this.preexistingFilters?.filters, 'field').map(filter => {
      uniqueFilters.push(filter.field);
    });

    this.additionalFilters.map(filter => {
      if (
        uniqueFilters.includes(filter.varName) &&
        this.filterForm.controls['primaryForm']['controls'][filter.varName][
          'value'
        ] &&
        !isEmpty(
          this.filterForm.controls['primaryForm']['controls'][filter.varName][
            'value'
          ]
        )
      ) {
        filterCount++;
      }
    });

    const formLevels = Object.keys(this.filterForm.controls);
    formLevels.map(level => {
      const formLevelKeys = Object.keys(
        this.filterForm.controls[level]['controls']
      );
      formLevelKeys.map(levelKey => {
        const formObj = this.filterForm.controls[level]['controls'][levelKey];
        if (
          formObj['value'] &&
          !isEmpty(formObj['value']) &&
          !uniqueFilters.includes(levelKey) &&
          this.additionalFilters.find(filter => filter.varName === levelKey)
        ) {
          uniqueFilters.push(levelKey);
          filterCount++;
        }
      });
    });

    return filterCount;
  }

  showSearchButton(keyString) {
    const hasSecondaryForm =
      this.formFields.secondaryForm && this.formFields.secondaryForm.length > 0;

    if (this.manualSearch) {
      if (this.gridName === 'CaseSearch') {
        if (keyString === 'primaryForm') {
          return true;
        } else {
          return false;
        }
      } else {
        if (keyString === 'primaryForm' && !hasSecondaryForm) {
          return true;
        } else if (keyString === 'secondaryForm' && hasSecondaryForm) {
          return true;
        }
      }
    } else {
      return false;
    }
  }

  hasConditionalFields(fieldGroup) {
    if (fieldGroup.find(field => field.conditionalParent)) {
      return true;
    }
    return false;
  }

  applyHiddenCols(event) {
    this.hiddenColsEmitter.emit(event);
  }

  applyColSort(event) {
    this.colSortEmitter.emit(event);
  }

  applyColOrder(event) {
    this.colOrderEmitter.emit(event);
  }
}
